/**
 * Composición del mensaje de WhatsApp. Es una SOLICITUD de pedido:
 * el texto deja claro que el restaurante confirma disponibilidad y total.
 */

import { CartLine, cartHasUnknownPrices, cartTotal, lineTotal, lineUnitPrice } from './cart';
import { ORDER_MODE_LABELS, OrderMode } from './settings';

export interface OrderContact {
  mode: OrderMode;
  table?: string;
  customerName?: string;
  address?: string;
  generalNote?: string;
}

export interface OrderMessageInput {
  restaurantName: string;
  lines: CartLine[];
  contact: OrderContact;
  menuUrl?: string;
}

/** Texto único para platillos sin precio publicado. Nunca se inventa un monto. */
export const PRICE_TO_CONFIRM = 'Precio por confirmar';

export function formatMoney(amount: number): string {
  return `$${Number(amount || 0).toLocaleString('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/** Campos obligatorios por modalidad. Devuelve los faltantes en lenguaje simple. */
export function missingContactFields(contact: OrderContact): string[] {
  const missing: string[] = [];
  if (contact.mode === 'pickup' && !contact.customerName?.trim()) missing.push('Nombre');
  if (contact.mode === 'delivery') {
    if (!contact.customerName?.trim()) missing.push('Nombre');
    if (!contact.address?.trim()) missing.push('Dirección');
  }
  return missing;
}

export function buildOrderMessage({ restaurantName, lines, contact, menuUrl }: OrderMessageInput): string {
  const parts: string[] = [];
  parts.push(`*Nuevo pedido — ${restaurantName}*`);
  parts.push('');

  lines.forEach((line, index) => {
    const total = lineTotal(line);
    const amount = total === null ? PRICE_TO_CONFIRM : formatMoney(total);
    parts.push(`${index + 1}. ${line.quantity}× ${line.itemName} — ${amount}`);
    line.options.forEach((option) => {
      const extra = option.priceModifier > 0 ? ` (+${formatMoney(option.priceModifier)})` : '';
      parts.push(`   • ${option.groupName}: ${option.optionName}${extra}`);
    });
    const unit = lineUnitPrice(line);
    if (line.options.length > 0 && unit !== null) {
      parts.push(`   • Precio unitario: ${formatMoney(unit)}`);
    }
    if (line.note?.trim()) {
      parts.push(`   • Nota: ${line.note.trim()}`);
    }
  });

  parts.push('');
  if (cartHasUnknownPrices(lines)) {
    parts.push(`*Total parcial: ${formatMoney(cartTotal(lines))}* (hay platillos con precio por confirmar)`);
  } else {
    parts.push(`*Total estimado: ${formatMoney(cartTotal(lines))}*`);
  }
  parts.push('');
  parts.push(`Modalidad: ${ORDER_MODE_LABELS[contact.mode]}`);

  if (contact.mode === 'table' && contact.table?.trim()) {
    parts.push(`Mesa: ${contact.table.trim()}`);
  }
  if (contact.customerName?.trim()) {
    parts.push(`Nombre: ${contact.customerName.trim()}`);
  }
  if (contact.mode === 'delivery' && contact.address?.trim()) {
    parts.push(`Dirección: ${contact.address.trim()}`);
  }
  if (contact.generalNote?.trim()) {
    parts.push(`Nota: ${contact.generalNote.trim()}`);
  }

  parts.push('');
  parts.push('Este pedido es una solicitud: espero su confirmación de disponibilidad, tiempo y total final.');
  if (menuUrl) parts.push(menuUrl);

  return parts.join('\n');
}
